import React, { useEffect, useState } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { observability } from '../../services/platformApi';

/**
 * Compact traffic-light health strip for the top nav.
 *
 * Replaces the big "module status" cards on Platform Overview / Adapter
 * Health pages by surfacing the same info in 6 small dots that live in
 * the AppBar — visible on every screen, not just the dedicated page.
 *
 *   ● MD  ● ST  ● AL  ● GR  ● DI  ● OR
 *
 * Hover any dot for adapter name + event count. Click any dot to jump
 * to the full Adapter Health page.
 */

const MODULES = [
  { key: 'marketdata',   short: 'MD' },
  { key: 'strategy',     short: 'ST' },
  { key: 'algo',         short: 'AL' },
  { key: 'grid',         short: 'GR' },
  { key: 'dispatch',     short: 'DI' },
  { key: 'orchestrator', short: 'OR' },
];

export default function AdapterHealthStrip() {
  const navigate = useNavigate();
  const [health, setHealth] = useState([]);
  const [stats, setStats] = useState([]);

  const refresh = async () => {
    try {
      const [h, s] = await Promise.all([
        observability.health(),
        observability.stats(),
      ]);
      setHealth(h || []);
      setStats(s || []);
    } catch { /* best effort */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <Stack
      direction="row" spacing={0.6} alignItems="center"
      sx={{
        px: 1.2, py: 0.5,
        borderRadius: 1.5,
        border: '1px solid rgba(255,255,255,0.08)',
        bgcolor: 'rgba(255,255,255,0.03)',
        cursor: 'pointer',
      }}
      onClick={() => navigate('/observability/health')}
    >
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 9, mr: 0.4, letterSpacing: 0.5 }}>
        ADAPTERS
      </Typography>
      {MODULES.map((m) => {
        const h = health.find((x) => x?.source === m.key);
        const s = stats.find((x) => x?.source === m.key);
        const eventCount = s?.stats?.reduce((a, r) => a + (r.count || 0), 0) || 0;
        const errorCount = s?.stats?.reduce((a, r) => a + (r.errors || 0), 0) || 0;
        const ok = !!h?.ok;
        const color = !h ? 'rgba(255,255,255,0.2)' : (ok ? (errorCount > 0 ? '#ffb800' : '#00ff88') : '#ff6b6b');
        const status = !h ? 'no data' : (ok ? (errorCount > 0 ? 'OK with errors' : 'OK') : (h.error || 'down'));
        const tip = (
          <Box sx={{ fontSize: 11 }}>
            <Box sx={{ fontWeight: 700, color: '#00d4ff' }}>{m.key}</Box>
            <Box>status: {status}</Box>
            <Box>adapter: {h?.adapter?.name || '—'}</Box>
            <Box>events: {eventCount}{errorCount > 0 ? ` · errors ${errorCount}` : ''}</Box>
            <Box sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.3 }}>click to open Adapter Health</Box>
          </Box>
        );
        return (
          <Tooltip key={m.key} title={tip} arrow placement="bottom">
            <Stack direction="row" spacing={0.4} alignItems="center" sx={{ px: 0.4 }}>
              <Box sx={{
                width: 8, height: 8, borderRadius: '50%',
                bgcolor: color,
                boxShadow: ok ? `0 0 5px ${color}` : 'none',
                transition: 'all 0.3s ease',
              }} />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 10, fontFamily: 'monospace' }}>
                {m.short}
              </Typography>
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
}
